import {
    ChangeDetectionStrategy,
    Component,
    DOCUMENT,
    ElementRef,
    inject,
    NgZone,
    OnDestroy,
    OnInit,
    Renderer2,
    viewChild
} from '@angular/core';

const TOOLTIP_SELECTOR = '[data-tooltip]';
const TOOLTIP_OFFSET = 4;
const VIEWPORT_MARGIN = 8;
const HALF = 2;

@Component({
    selector: 'cal-hover-tooltip',
    template: '<div #surface class="mat-mdc-tooltip-surface"></div>',
    styleUrl: './hover-tooltip.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'cal-tooltip',
        role: 'tooltip'
    }
})
export class HoverTooltipComponent implements OnInit, OnDestroy {
    private document = inject(DOCUMENT);
    private ngZone = inject(NgZone);
    private renderer = inject(Renderer2);
    private host: HTMLElement = inject(ElementRef).nativeElement;
    private surface = viewChild.required<ElementRef<HTMLElement>>('surface');

    private trigger: Element | null = null;
    private cleanups: (() => void)[] = [];

    ngOnInit(): void {
        this.ngZone.runOutsideAngular(() => {
            this.cleanups = [
                this.renderer.listen(this.document, 'mouseover', this.onMouseOver),
                this.renderer.listen(this.document, 'mousedown', this.hide),
                this.renderer.listen(this.document, 'wheel', this.hide, { passive: true }),
                this.renderer.listen(this.document, 'scroll', this.hide, { capture: true, passive: true })
            ];
        });
    }

    ngOnDestroy(): void {
        this.cleanups.forEach((cleanup) => cleanup());
    }

    private onMouseOver = (event: MouseEvent): void => {
        if (event.buttons) {
            return;
        }

        const trigger = (event.target as Element | null)?.closest?.(TOOLTIP_SELECTOR) ?? null;

        if (trigger === this.trigger) {
            return;
        }

        if (trigger) {
            this.show(trigger);
        } else {
            this.hide();
        }
    };

    private show(trigger: Element): void {
        this.trigger = trigger;
        this.surface().nativeElement.textContent = trigger.getAttribute('data-tooltip');
        this.host.style.display = 'block';

        const triggerRect = trigger.getBoundingClientRect();
        const { width, height } = this.host.getBoundingClientRect();
        const maxLeft = window.innerWidth - width - VIEWPORT_MARGIN;
        const left = Math.max(VIEWPORT_MARGIN, Math.min(triggerRect.left + (triggerRect.width - width) / HALF, maxLeft));
        const fitsBelow = triggerRect.bottom + TOOLTIP_OFFSET + height <= window.innerHeight - VIEWPORT_MARGIN;
        const top = fitsBelow
            ? triggerRect.bottom + TOOLTIP_OFFSET
            : triggerRect.top - TOOLTIP_OFFSET - height;

        this.host.style.transform = `translate(${left}px, ${top}px)`;
    }

    private hide = (): void => {
        if (this.trigger) {
            this.trigger = null;
            this.host.style.display = 'none';
        }
    };
}
